// Release B-Fidelity-2a · Slice C-detail — Owner Access-Review Detail.
// Route: /admin/access-requests/:id
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
// Release C-2 (P1) — Open in Microsoft 365 Mail for the Access Request detail surface.
import OpenInMicrosoftMailButton from "../../design-system/microsoft365/OpenInMicrosoftMailButton";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "timeline", label: "Timeline" },
  { key: "questionnaire", label: "Questionnaire" },
  { key: "documents", label: "Documents" },
  { key: "schedule", label: "Schedule" },
  { key: "decision", label: "Decision" },
  { key: "invitation", label: "Invitation / Activation" },
  { key: "audit", label: "Audit" },
];

const STATE_COLOR = {
  submitted: "#94a3b8", email_verified: "#0ea5e9", owner_review: "#f59e0b",
  needs_classification: "#a855f7", questionnaire_requested: "#f97316",
  questionnaire_submitted: "#14b8a6", additional_information_requested: "#eab308",
  interview_scheduled: "#6366f1", approved: "#22c55e", rejected: "#dc2626",
  blocked: "#7f1d1d", invitation_sent: "#22c55e", invitation_accepted: "#059669",
  activated: "#166534", withdrawn: "#64748b", expired: "#64748b",
};

function StateChip({ s }) {
  const c = STATE_COLOR[s] || "#64748b";
  return <span data-testid={`ard-state-${s}`} style={{
    display: "inline-block", padding: "3px 10px", borderRadius: 9999,
    background: c + "22", color: c, border: `1px solid ${c}55`,
    fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
  }}>{s}</span>;
}

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }

export default function AccessRequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/access-requests/${id}`);
      setRow(r.data);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === "access_request_not_found" ? "Access request not found." : "Failed to load.");
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <AdminAppShell current="/admin/access-requests"><div style={{ padding: 24 }}>Loading…</div></AdminAppShell>;
  if (!row) return (
    <AdminAppShell current="/admin/access-requests">
      <div style={{ padding: 24 }}>
        <p>Access request not found.</p>
        <Link to="/admin/access-requests" data-testid="ard-back-list">← Back to inbox</Link>
      </div>
    </AdminAppShell>
  );

  return (
    <AdminAppShell current="/admin/access-requests">
      <div data-testid="access-request-detail" style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div>
            <Link to="/admin/access-requests" data-testid="ard-back-link" style={{ fontSize: 12, color: "#475569" }}>← Back to access requests</Link>
            <h1 style={{ fontSize: 24, margin: "8px 0 4px", fontWeight: 700 }}>{row.applicant_snapshot?.full_legal_name || "Access request"}</h1>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <StateChip s={row.status} />
              <span style={{ fontSize: 12, color: "#475569" }}>
                {row.applicant_snapshot?.email} · {row.applicant_snapshot?.phone || "—"} · {row.applicant_snapshot?.city}/{row.applicant_snapshot?.state}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
              Requested relationship: <b>{row.requested_relationship}</b>
              {row.needs_classification && <span data-testid="ard-needs-class" style={{ marginLeft: 8, color: "#a855f7" }}>· needs classification</span>}
              {row.duplicate_of && <span style={{ marginLeft: 8, color: "#dc2626" }}>· duplicate of {row.duplicate_of.slice(0, 8)}</span>}
              {row.spam_flag && <span style={{ marginLeft: 8, color: "#dc2626" }}>· spam-flagged</span>}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
            {/* FINAL-POLISH P2 — Open in Microsoft 365 Mail promoted to the record header for Owner/Admin. */}
            <OpenInMicrosoftMailButton surface="access_request_detail" recordType="access_request" recordId={row.id} />
            <ActionBar row={row} onChanged={load} />
          </div>
        </div>

        <div style={{ marginTop: 20, borderBottom: "1px solid #e2e8f0", display: "flex", gap: 4, flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <button key={t.key} data-testid={`ard-tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    style={{ padding: "10px 14px", background: "none",
                              borderTop: "none", borderLeft: "none", borderRight: "none",
                              borderBottomWidth: 2, borderBottomStyle: "solid",
                              borderBottomColor: tab === t.key ? "#12233F" : "transparent",
                              fontWeight: tab === t.key ? 600 : 400, cursor: "pointer",
                              color: tab === t.key ? "#12233F" : "#475569" }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 20 }}>
          {tab === "overview" && <TabOverview row={row} />}
          {tab === "timeline" && <TabTimeline row={row} />}
          {tab === "questionnaire" && <TabQuestionnaire row={row} onChanged={load} />}
          {tab === "documents" && <TabDocuments row={row} onChanged={load} />}
          {tab === "schedule" && <TabSchedulePlaceholder />}
          {tab === "decision" && <TabDecision row={row} onChanged={load} />}
          {tab === "invitation" && <TabInvitation row={row} />}
          {tab === "audit" && <TabAudit row={row} />}
        </div>
      </div>
    </AdminAppShell>
  );
}

// ---- Action bar wired to all state transitions ----

function ActionBar({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const canApprove = row.status === "owner_review" || row.status === "questionnaire_submitted" || row.status === "interview_scheduled";
  const canReject = ["owner_review", "questionnaire_submitted", "interview_scheduled"].includes(row.status);
  const canBlock = ["submitted", "owner_review"].includes(row.status);
  const canSendQuestionnaire = row.status === "owner_review";
  const canRequestInfo = ["owner_review", "questionnaire_submitted"].includes(row.status);
  const canClassify = row.status === "needs_classification";

  const post = async (path, payload = {}, okMsg = "Done") => {
    setBusy(true);
    try {
      await api.post(`/admin/access-requests/${row.id}${path}`, payload);
      toast.success(okMsg);
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d?.code || "Action failed");
    } finally { setBusy(false); }
  };

  const doClassify = async () => {
    const rel = window.prompt("Resolve to which relationship key? (e.g. client, subcontractor, employee)");
    if (!rel) return;
    await post("/classify", { resolved_relationship: rel }, "Classified");
  };
  const doSendQuestionnaire = async () => {
    const msg = window.prompt("Optional message to include with the questionnaire link:", "");
    await post("/questionnaire/send", { message_to_applicant: msg || null }, "Questionnaire sent");
  };
  const doRequestInfo = async () => {
    const p = window.prompt("What information do you need from the applicant?");
    if (!p) return;
    await post("/request-info", { reason_code: "owner_requests_info", prompt_to_applicant: p }, "Info requested");
  };
  const doReject = async () => {
    const publicNote = window.prompt("Public note visible to the applicant (safe wording):", "After review, we're not able to move forward at this time.");
    const internalNote = window.prompt("Internal note (Owner only, not sent to applicant):", "");
    await post("/reject", { reason_code: "owner_rejected", reason_note_public: publicNote, reason_note_internal: internalNote }, "Rejected");
  };
  const doBlock = async () => {
    const internalNote = window.prompt("Internal note (why is this being blocked?):", "");
    if (internalNote === null) return;
    await post("/block", { reason_code: "owner_blocked", reason_note_internal: internalNote }, "Blocked");
  };
  const doApprove = async () => {
    const roleKey = window.prompt("Resolved role_key to grant (e.g. CLIENT, SUBCONTRACTOR, EMPLOYEE):");
    if (!roleKey) return;
    await post("/approve", { resolved_role_key: roleKey.toUpperCase(), portal: "operator" }, "Approved + invitation sent");
  };
  const doIssueDocsToken = async () => {
    try {
      const r = await api.post(`/admin/access-requests/${row.id}/documents/token`, {});
      window.prompt("Share this upload link with the applicant:", r.data.upload_url);
    } catch (e) {
      toast.error("Failed to mint upload token");
    }
  };

  return (
    <div data-testid="ard-action-bar" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {canClassify && <Button data-testid="ard-action-classify" onClick={doClassify} disabled={busy}>Classify</Button>}
      {canSendQuestionnaire && <Button data-testid="ard-action-send-questionnaire" onClick={doSendQuestionnaire} disabled={busy}>Send questionnaire</Button>}
      {canRequestInfo && <Button data-testid="ard-action-request-info" variant="secondary" onClick={doRequestInfo} disabled={busy}>Request info</Button>}
      <Button data-testid="ard-action-issue-doc-token" variant="secondary" onClick={doIssueDocsToken} disabled={busy}>Docs upload link</Button>
      {canApprove && <Button data-testid="ard-action-approve" onClick={doApprove} disabled={busy}>Approve + invite</Button>}
      {canReject && <Button data-testid="ard-action-reject" variant="destructive" onClick={doReject} disabled={busy}>Reject</Button>}
      {canBlock && <Button data-testid="ard-action-block" variant="destructive" onClick={doBlock} disabled={busy}>Block</Button>}
    </div>
  );
}

// ---- Tabs ----

function KV({ k, v }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 12, padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#64748b" }}>{k}</div>
      <div style={{ fontSize: 13, color: "#0f172a" }}>{v == null || v === "" ? "—" : v}</div>
    </div>
  );
}

function TabOverview({ row }) {
  const s = row.applicant_snapshot || {};
  return (
    <Card><CardContent style={{ padding: 20 }}>
      <div data-testid="ard-tab-overview-body" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        <div>
          <h3 style={{ marginTop: 0 }}>Applicant snapshot</h3>
          <KV k="Full legal name" v={s.full_legal_name} />
          <KV k="Preferred name" v={s.preferred_name} />
          <KV k="Business" v={s.business_name} />
          <KV k="Email" v={<>{s.email} {s.email_verified && <span style={{ color: "#22c55e", fontSize: 11, marginLeft: 6 }}>✓ verified</span>}</>} />
          <KV k="Phone" v={s.phone} />
          <KV k="City / State" v={`${s.city || ""} / ${s.state || ""}`} />
        </div>
        <div>
          <h3 style={{ marginTop: 0 }}>Request</h3>
          <KV k="Reference ID" v={row.id?.slice(0, 8).toUpperCase()} />
          <KV k="Relationship" v={row.requested_relationship} />
          <KV k="Other description" v={row.other_relationship_description} />
          <KV k="Referrer" v={row.referrer_name} />
          <KV k="Reason" v={<span style={{ whiteSpace: "pre-wrap" }}>{row.reason}</span>} />
          <KV k="Submitted" v={fmt(row.created_at)} />
          <KV k="Last activity" v={fmt(row.last_activity_at)} />
        </div>
      </div>
    </CardContent></Card>
  );
}

function TabTimeline({ row }) {
  const history = row.state_history || [];
  return (
    <Card><CardContent style={{ padding: 20 }}>
      <div data-testid="ard-tab-timeline-body">
        {history.length === 0 && <p style={{ color: "#64748b", fontSize: 13 }}>No state transitions yet.</p>}
        {history.map((h) => (
          <div key={h.id} style={{ padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div>
                {h.from ? <StateChip s={h.from} /> : <span style={{ fontSize: 11, color: "#64748b" }}>note</span>}
                {h.to && <> → <StateChip s={h.to} /></>}
                <span style={{ marginLeft: 8, fontSize: 11, color: "#64748b" }}>
                  {h.actor_role} {h.actor_user_id?.slice(0, 8)}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#64748b" }}>{fmt(h.at || h.created_at)}</div>
            </div>
            {h.reason_code && <div style={{ fontSize: 11, color: "#475569" }}>code: {h.reason_code}</div>}
            {h.reason_note_public && <div style={{ fontSize: 12 }}>public: <i>{h.reason_note_public}</i></div>}
            {h.reason_note_internal && <div style={{ fontSize: 12, color: "#475569" }}>internal: {h.reason_note_internal}</div>}
            {h.body && <div style={{ fontSize: 13 }}>{h.body}</div>}
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function TabQuestionnaire({ row, onChanged }) {
  const q = row.questionnaire_ref;
  const [schema, setSchema] = useState(null);
  useEffect(() => {
    if (q?.schema_role_key) {
      api.get(`/admin/access-requests/questionnaire-schemas/${q.schema_role_key}`)
        .then((r) => setSchema(r.data)).catch(() => setSchema(null));
    }
  }, [q?.schema_role_key]);
  if (!q) return (
    <Card><CardContent style={{ padding: 20 }}>
      <p data-testid="ard-q-none" style={{ color: "#64748b" }}>No questionnaire has been sent yet.</p>
      <p style={{ fontSize: 12, color: "#475569" }}>Use "Send questionnaire" in the action bar to email the applicant a role-scoped form.</p>
    </CardContent></Card>
  );
  const answers = q.submitted_answers || q.draft_answers || {};
  return (
    <Card><CardContent style={{ padding: 20 }}>
      <div data-testid="ard-tab-questionnaire-body">
        <KV k="Schema" v={`${q.schema_role_key} v${q.schema_version}`} />
        <KV k="Sent at" v={fmt(q.sent_at)} />
        <KV k="Submitted at" v={fmt(q.submitted_at)} />
        {q.message_to_applicant && <KV k="Message sent" v={q.message_to_applicant} />}
        <h3 style={{ marginTop: 20 }}>Answers</h3>
        {!q.submitted_answers && <p style={{ color: "#64748b", fontSize: 12 }}>Applicant has not yet submitted.</p>}
        {schema && (schema.sections || []).map((sec) => (
          <div key={sec.id} style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#475569" }}>{sec.title}</div>
            {(sec.fields || []).map((f) => (
              <KV key={f.id} k={f.label} v={renderAnswer(answers[f.id], f)} />
            ))}
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function renderAnswer(v, f) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) {
    const opts = new Map((f.options || []).map(o => [o.value, o.label]));
    return v.map(x => opts.get(x) || x).join(", ");
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (f.type === "select") {
    const opt = (f.options || []).find(o => o.value === v);
    return opt ? opt.label : String(v);
  }
  return String(v);
}

function TabDocuments({ row, onChanged }) {
  const [docs, setDocs] = useState(row.documents || []);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => { setDocs(row.documents || []); }, [row.documents]);

  const verify = async (docId) => {
    const notes = window.prompt("Optional verification notes:") || "";
    try {
      await api.post(`/admin/access-requests/${row.id}/documents/${docId}/verify`, { notes });
      toast.success("Verified");
      onChanged();
    } catch (e) { toast.error("Failed to verify"); }
  };
  const reject = async (docId) => {
    const internal = window.prompt("Internal reason (required):");
    if (!internal) return;
    const publicN = window.prompt("Optional public message to applicant:") || "";
    try {
      await api.post(`/admin/access-requests/${row.id}/documents/${docId}/reject`,
                     { reason_note_internal: internal, reason_note_public: publicN });
      toast.success("Rejected");
      onChanged();
    } catch (e) { toast.error("Failed to reject"); }
  };
  const download = (docId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-requests/${row.id}/documents/${docId}/download`;
    window.open(url, "_blank");
  };

  return (
    <Card><CardContent style={{ padding: 20 }}>
      <div data-testid="ard-tab-documents-body">
        {/* 3b retrofit — universal InformationIntake control tied to this access_request record. */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Documents on this access request</h3>
          <InformationIntake
            recordType="access_request" recordId={row.id}
            category="other" confidentiality="confidential"
            capabilities={["upload", "photo", "scan", "paste", "manual", "request_info"]}
            onChanged={() => { setReloadKey((k) => k + 1); onChanged && onChanged(); }}
          />
        </div>
        <AttachmentsPanel recordType="access_request" recordId={row.id} reload={reloadKey} />
        <hr style={{ margin: "16px 0", border: 0, borderTop: "1px solid #e2e8f0" }} />
        <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, margin: 0 }}>
          Legacy Slice E documents (applicant-uploaded)
        </p>
        {docs.length === 0 && <p style={{ color: "#64748b", fontSize: 13 }}>No legacy documents uploaded.</p>}
        {docs.map((d) => (
          <div key={d.id} data-testid={`ard-doc-${d.id}`}
               style={{ padding: 12, border: "1px solid #e2e8f0", borderRadius: 4, marginBottom: 8,
                        display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontWeight: 600 }}>{d.filename}</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                {d.category} · {d.mime} · {(d.size/1024).toFixed(1)} KB · {fmt(d.uploaded_at)}
              </div>
              {d.notes && <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>notes: {d.notes}</div>}
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>sha256: {d.sha256}</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <StateChip s={d.status} />
              <Button data-testid={`ard-doc-download-${d.id}`} size="sm" variant="secondary" onClick={() => download(d.id)}>Download</Button>
              {d.status !== "verified" && <Button data-testid={`ard-doc-verify-${d.id}`} size="sm" onClick={() => verify(d.id)}>Verify</Button>}
              {d.status !== "rejected" && <Button data-testid={`ard-doc-reject-${d.id}`} size="sm" variant="destructive" onClick={() => reject(d.id)}>Reject</Button>}
            </div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function TabSchedulePlaceholder() {
  return <TabScheduleLive />;
}

function TabScheduleLive() {
  const { id } = useParams();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/access-requests/${id}/appointments`);
      setAppts(r.data.items || []);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "provider_authorization_required") {
        setAppts({ _providerError: true });
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const invite = async () => {
    const method = window.prompt("Method hint (Phone / Teams / Video / ERF_Office / Project_Site / Other) — leave blank for all", "Phone");
    const msg = window.prompt("Optional message to include in the scheduling email:", "");
    try {
      const r = await api.post(`/admin/access-requests/${id}/schedule/send-invite`,
                                { method_hint: method || null, message_to_applicant: msg || null, days_window: 30 });
      window.prompt("Share this link with the applicant:", r.data.scheduling_url);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Failed to send invite"); }
  };
  const action = async (apptId, act) => {
    let payload = {};
    if (act === "reschedule") {
      const newTs = window.prompt("New starts_at (ISO-8601, e.g. 2026-10-01T14:00:00+00:00):");
      if (!newTs) return;
      const newM = window.prompt("New method (optional, blank to keep):") || null;
      payload = { new_starts_at: newTs, new_method: newM };
    }
    const note = window.prompt("Internal reason / note:") || "";
    payload.reason_note_internal = note;
    try {
      await api.post(`/admin/access-requests/${id}/schedule/${apptId}/${act}`, payload);
      toast.success(`${act} ok`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "action_failed"); }
  };
  return (
    <Card><CardContent style={{ padding: 20 }}>
      <div data-testid="ard-tab-schedule-body">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Scheduling</h3>
          <Button data-testid="ard-schedule-send-invite" size="sm" onClick={invite}>Send scheduling invite</Button>
        </div>
        {loading && <p style={{ color: "#64748b", fontSize: 13, marginTop: 10 }}>Loading…</p>}
        {!loading && appts._providerError && <p style={{ color: "#dc2626", fontSize: 12 }}>Calendar provider requires Owner authorisation. Grant consent in Integrations → Calendar.</p>}
        {!loading && Array.isArray(appts) && appts.length === 0 && (
          <p style={{ color: "#64748b", fontSize: 13, marginTop: 10 }}>No appointments booked yet. Send the applicant a scheduling invite to release available times.</p>
        )}
        {Array.isArray(appts) && appts.map((a) => (
          <div key={a.id} data-testid={`ard-appt-${a.id}`}
               style={{ padding: 12, border: "1px solid #e2e8f0", borderRadius: 4, marginTop: 10, display: "flex", gap: 10, justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontWeight: 600 }}>{fmt(a.starts_at)} → {fmt(a.ends_at)}</div>
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>Method: <b>{a.method}</b> · State: <b>{a.state}</b> · Auto-confirmed: {a.auto_confirmed ? "yes" : "no"}</div>
              {a.notes_public && <div style={{ fontSize: 12, marginTop: 4 }}>Applicant note: {a.notes_public}</div>}
              {a.notes_internal && <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>Internal: {a.notes_internal}</div>}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {a.state === "proposed" && <Button data-testid={`ard-appt-confirm-${a.id}`} size="sm" onClick={() => action(a.id, "confirm")}>Confirm</Button>}
              {(a.state === "proposed" || a.state === "confirmed") && (
                <>
                  <Button data-testid={`ard-appt-resched-${a.id}`} size="sm" variant="secondary" onClick={() => action(a.id, "reschedule")}>Reschedule</Button>
                  <Button data-testid={`ard-appt-cancel-${a.id}`} size="sm" variant="destructive" onClick={() => action(a.id, "cancel")}>Cancel</Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function TabDecision({ row }) {
  const d = row.decision;
  return (
    <Card><CardContent style={{ padding: 20 }}>
      <div data-testid="ard-tab-decision-body">
        {!d && <p style={{ color: "#64748b", fontSize: 13 }}>No decision recorded yet.</p>}
        {d && (
          <>
            <KV k="Outcome" v={d.outcome} />
            <KV k="Decided by" v={d.by} />
            <KV k="Decided at" v={fmt(d.at)} />
            <KV k="Resolved role_key" v={d.resolved_role_key} />
            <KV k="Portal" v={d.portal} />
            <KV k="Public reason" v={d.reason_public} />
            <KV k="Internal reason" v={d.reason_internal} />
          </>
        )}
      </div>
    </CardContent></Card>
  );
}

function TabInvitation({ row }) {
  return (
    <Card><CardContent style={{ padding: 20 }}>
      <div data-testid="ard-tab-invitation-body">
        <KV k="Invitation ref (user_id)" v={row.invitation_ref} />
        <KV k="Activation ref" v={row.activation_ref} />
        <KV k="Current state" v={<StateChip s={row.status} />} />
        <p style={{ marginTop: 16, fontSize: 12, color: "#475569" }}>
          Approving the request auto-mints a single-use invite token (7-day TTL) and enqueues the
          "you're invited" email via the Release A pipeline. Applicant activation moves the state
          from <code>invitation_sent</code> → <code>invitation_accepted</code> → <code>activated</code>.
        </p>
      </div>
    </CardContent></Card>
  );
}

function TabAudit({ row }) {
  const attempts = row.notification_attempts || [];
  return (
    <Card><CardContent style={{ padding: 20 }}>
      <div data-testid="ard-tab-audit-body">
        <h3 style={{ marginTop: 0 }}>Notification attempts ({attempts.length})</h3>
        {attempts.length === 0 && <p style={{ color: "#64748b", fontSize: 13 }}>No notifications correlated to this request yet.</p>}
        {attempts.map((n) => (
          <div key={n.id} style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <div>
                <b style={{ fontSize: 13 }}>{n.template_key}</b>
                <span style={{ marginLeft: 8, fontSize: 11, color: "#64748b" }}>→ {n.recipient}</span>
              </div>
              <div style={{ fontSize: 11, color: "#64748b" }}>{fmt(n.queued_at)}</div>
            </div>
            <div style={{ fontSize: 11, marginTop: 2 }}>
              state: <b>{n.state}</b> · delivery: <b>{n.delivery_state}</b>
              {n.provider_message_id && <> · provider_id: <code>{n.provider_message_id.slice(0, 24)}</code></>}
            </div>
            {n.sanitized_error_reason && <div style={{ fontSize: 11, color: "#dc2626" }}>error: {n.sanitized_error_reason}</div>}
          </div>
        ))}
        <div style={{ marginTop: 20, fontSize: 12, color: "#475569" }}>
          Every state transition, note, and document action on this request is also recorded in the append-only Owner Audit ledger.
          Search this request's ID in <Link to="/admin/audit-trust/ledger" style={{ textDecoration: "underline" }}>Audit &amp; Trust</Link> for the full chain.
        </div>
      </div>
    </CardContent></Card>
  );
}
